import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { getSEOPages } from "../../../services/seoPageService";
import classes from "./styles/SEOPageListing.module.css";

// ── Helpers ──────────────────────────────────────────────────
const CATEGORY_ICONS = {
  "Analyst": "🔍",
  "Information Technology": "💻",
  "Finance": "📊",
  "Healthcare": "🏥",
  "Education": "📚",
  "Marketing": "📢",
  "Sales": "🤝",
  "Engineering": "⚙️",
  "Design": "🎨",
  "Legal": "⚖️",
  "Human Resources": "👥",
  "Operations": "🏭",
  "Customer Support": "🎧",
  "Logistics": "🚚",
  "Media & Communication": "📡",
  "Other": "💼",
  default: "💼",
};

const getCategoryIcon = (cat) => CATEGORY_ICONS[cat] || CATEGORY_ICONS.default;

// ── Skeleton ─────────────────────────────────────────────────
const CardSkeleton = () => (
  <div className={classes.cardSkeleton}>
    <div className={classes.skBadge} />
    <div className={classes.skTitle} />
    <div className={classes.skSubtitle} />
    <div className={classes.skSubtitle} style={{ width: "60%" }} />
    <div className={classes.skFooter}>
      <div className={classes.skChip} />
      <div className={classes.skChip} />
    </div>
  </div>
);

// ── SEO Card ─────────────────────────────────────────────────
const SEOCard = ({ item, onClick }) => {
  const icon = getCategoryIcon(item.category);
  const keywords = item.keywords?.slice(0, 3) || [];

  return (
    <article
      className={classes.card}
      onClick={() => onClick(item.slug)}
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick(item.slug)}
      role="button"
      aria-label={`View ${item.title}`}
    >
      <div className={classes.cardInner}>
        <div className={classes.cardTop}>
          <span className={classes.categoryBadge}>
            <span className={classes.catIcon}>{icon}</span>
            {item.category || "Jobs"}
          </span>
          <span className={classes.locationPill}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {item.location}
          </span>
        </div>

        <h2 className={classes.cardTitle}>{item.title}</h2>
        <p className={classes.cardSubtitle}>{item.subtitle}</p>

        <div className={classes.cardFooter}>
          <div className={classes.keywords}>
            {keywords.map((kw) => (
              <span key={kw} className={classes.keyword}>{kw}</span>
            ))}
          </div>
          <span className={classes.arrow}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </span>
        </div>
      </div>
      <div className={classes.cardGlow} />
    </article>
  );
};

// ── Main ─────────────────────────────────────────────────────
const SEOPageListing = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [pages, setPages] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [location, setLocation] = useState(searchParams.get("location") || "");
  // const [country] = useState(searchParams.get("country") || "India");
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("page")) || 1);

  const [inputSearch, setInputSearch] = useState(search);
  const [inputLocation, setInputLocation] = useState(location);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSEOPages({ page: currentPage, location, search });
      setPages(data.data || []);
      setPagination(data.pagination || {});
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, location, search]);

  useEffect(() => { load(); }, [load]);

  // Sync URL params
  useEffect(() => {
    const p = {};
    if (search) p.search = search;
    if (location) p.location = location;
    if (currentPage > 1) p.page = String(currentPage);
    // if (country) p.country = country;
    setSearchParams(p, { replace: true });
  }, [search, location, currentPage, setSearchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(inputSearch);
    setLocation(inputLocation);
    setCurrentPage(1);
  };

  const handleClear = () => {
    setInputSearch("");
    setInputLocation("");
    setSearch("");
    setLocation("");
    setCurrentPage(1);
  };

  const handleCardClick = (slug) => navigate(`/jobs/${slug}`);

  const hasFilters = search || location;

  return (
    <div className={classes.page}>
      <Helmet>
        <title>Browse Job Categories | FetchJobs India</title>
        <meta name="description" content="Explore curated job listings by role and city across India. Find Software, Finance, Marketing jobs and more." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.fetchjobs.co/jobs" />
      </Helmet>

      {/* ── Hero ── */}
      <section className={classes.hero}>
        <div className={classes.heroBg}>
          <div className={classes.heroDot} />
          <div className={classes.heroDot} />
        </div>
        <div className={classes.heroContent}>
          <p className={classes.heroEyebrow}>Browse Opportunities</p>
          <h1 className={classes.heroTitle}>
            Find Your Next<br />
            <span className={classes.heroAccent}>Dream Role</span>
          </h1>
          <p className={classes.heroDesc}>
            Curated job guides across top cities and roles in India.
          </p>
        </div>

        {/* Search bar */}
        <form className={classes.searchBar} onSubmit={handleSearch} role="search">
          <div className={classes.searchField}>
            <svg className={classes.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              className={classes.searchInput}
              placeholder="Role, skill, keyword..."
              value={inputSearch}
              onChange={(e) => setInputSearch(e.target.value)}
            />
          </div>
          <div className={classes.searchDivider} />
          <div className={classes.searchField}>
            <svg className={classes.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
            </svg>
            <input
              type="text"
              className={classes.searchInput}
              placeholder="City, e.g. Bengaluru..."
              value={inputLocation}
              onChange={(e) => setInputLocation(e.target.value)}
            />
          </div>
          <button type="submit" className={classes.searchBtn}>Search</button>
        </form>
      </section>

      {/* ── Results ── */}
      <section className={classes.results}>
        <div className={classes.resultsHeader}>
          <div className={classes.resultsInfo}>
            {!loading && (
              <>
                <span className={classes.resultCount}>
                  {pagination.total || 0} job guides
                </span>
                {hasFilters && (
                  <span className={classes.activeFilters}>
                    {search && (
                      <span className={classes.filterTag}>
                        {search}
                        <button onClick={handleClear} className={classes.filterX}>×</button>
                      </span>
                    )}
                    {location && !search && (
                      <span className={classes.filterTag}>
                        {location}
                        <button onClick={handleClear} className={classes.filterX}>×</button>
                      </span>
                    )}
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        {error && (
          <div className={classes.errorBox}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" />
            </svg>
            Could not load listings. Please try again.
          </div>
        )}

        {/* Grid */}
        <div className={classes.grid}>
          {loading
            ? Array.from({ length: 9 }).map((_, i) => <CardSkeleton key={i} />)
            : pages.length === 0
            ? (
              <div className={classes.empty}>
                <span className={classes.emptyIcon}>🔍</span>
                <p>No job guides found for your search.</p>
                <button className={classes.clearBtn} onClick={handleClear}>Clear filters</button>
              </div>
            )
            : pages.map((item) => (
              <SEOCard key={item._id} item={item} onClick={handleCardClick} />
            ))
          }
        </div>

        {/* Pagination */}
        {!loading && pagination.totalPages > 1 && (
          <div className={classes.pagination}>
            <button
              className={classes.pageBtn}
              disabled={currentPage === 1}
              onClick={() => { setCurrentPage(p => p - 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Previous
            </button>

            <div className={classes.pageNumbers}>
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                .filter(n => n === 1 || n === pagination.totalPages || Math.abs(n - currentPage) <= 1)
                .reduce((acc, n, idx, arr) => {
                  if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…");
                  acc.push(n);
                  return acc;
                }, [])
                .map((n, i) =>
                  n === "…"
                    ? <span key={`ellipsis-${i}`} className={classes.ellipsis}>…</span>
                    : <button
                        key={n}
                        className={`${classes.pageNum} ${n === currentPage ? classes.pageNumActive : ""}`}
                        onClick={() => { setCurrentPage(n); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                      >{n}</button>
                )
              }
            </div>

            <button
              className={classes.pageBtn}
              disabled={currentPage === pagination.totalPages}
              onClick={() => { setCurrentPage(p => p + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            >
              Next
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default SEOPageListing;
